// src/components/Proyectos/ProyectoTareas.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import {
  getTareasByProyecto,
  createTarea,
  updateTarea,
  deleteTarea,
  getTareasDefinicion,
  getDisenos,
  getMuestras,
  getMateriales,
  getResponsablesTodos,
  getTareasParticipantes,
  postTareasParticipante,
  deleteTareasParticipante,
} from "../../api/Proyectos/ProyectosTareasApi";
import { getAllProyectos } from "../../api/Proyectos/proyectoApi";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import dayjs from "dayjs";

import { motion, AnimatePresence } from "framer-motion";

import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";

import DocumentosTareas from "./DocumentosTareas";
import {
  pageFieldSx,
  pageHeroShellSx,
  pageContentCardSx,
  pageSectionTitleSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

/** Excluye Admin del select de responsable / participantes. */
const isSelectableUser = (u) => {
  if (!u) return false;
  if (u.isAdmin) return false;
  const name = String(u.username || "").trim().toLowerCase();
  return name !== "admin";
};

const emptyForm = {
  RelacionTipo: "",
  RelacionId: null,
  TituloId: "",
  Descripcion: "",
  ResponsableId: "",
  Estado: "Pending",
  Prioridad: 2,
  FechaInicio: dayjs().format("YYYY-MM-DD"),
  FechaLimite: "",
};

const ProyectoTareas = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = pageFieldSx(theme);
  const accent = theme.palette.primary.main;
  const sectionCardSx = {
    ...pageContentCardSx(theme, accent),
    p: { xs: 1.5, sm: 2 },
  };
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { id } = useParams();
  const [tareas, setTareas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [relacionLista, setRelacionLista] = useState([]);
  const [proyectoNombre, setProyectoNombre] = useState("");
  const [tareasDefinicion, setTareasDefinicion] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [tareaAEliminar, setTareaAEliminar] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [initialParticipanteIds, setInitialParticipanteIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [disenos, setDisenos] = useState([]);
  const [muestras, setMuestras] = useState([]);
  const [materiales, setMateriales] = useState([]);

  // estado para controlar cuál está expandida
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);

  //Nuevo estado para guardar conteos de documentos
  const [docCounts, setDocCounts] = useState({});
  const handleDocCountChange = (tareaId, count) => {
    setDocCounts((prev) => ({ ...prev, [tareaId]: count }));
  };

  const estadoColors = {
    Pending: "#e65100",
    Running: "#1565c0",
    Done: "#2e7d32",
  };

  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    getTareasDefinicion().then(setTareasDefinicion).catch(console.error);
    getResponsablesTodos()
      .then((users) => setUsuarios((users || []).filter(isSelectableUser)))
      .catch(console.error);
    getDisenos().then(setDisenos).catch(console.error);
    getMuestras().then(setMuestras).catch(console.error);
    getMateriales().then(setMateriales).catch(console.error);

    if (id) getTareasByProyecto(id).then(setTareas);

    const loadProyecto = async () => {
      try {
        const proyectos = await getAllProyectos();
        const proyecto = proyectos.find((p) => p.id === Number(id));
        if (proyecto) setProyectoNombre(proyecto.subject || `Proyecto ${id}`);
      } catch (error) {
        console.error("❌ Error al cargar proyecto:", error);
      }
    };
    loadProyecto();
  }, [id]);

  const resetDialog = () => {
    setOpenDialog(false);
    setEditingTarea(null);
    setFormData({ ...emptyForm });
    setParticipantes([]);
    setInitialParticipanteIds([]);
  };

  const syncParticipantes = async (tareaId, responsableId) => {
    const nextIds = participantes
      .map((p) => Number(p.id))
      .filter((uid) => Number.isFinite(uid) && uid !== Number(responsableId));
    const prevIds = initialParticipanteIds.filter(
      (uid) => uid !== Number(responsableId),
    );
    const toAdd = nextIds.filter((uid) => !prevIds.includes(uid));
    const toRemove = prevIds.filter((uid) => !nextIds.includes(uid));
    await Promise.all([
      ...toAdd.map((userId) =>
        postTareasParticipante(tareaId, { UserId: userId }),
      ),
      ...toRemove.map((userId) => deleteTareasParticipante(tareaId, userId)),
    ]);
    return nextIds;
  };

  useEffect(() => {
    const loadRelacion = async () => {
      try {
        if (formData.RelacionTipo === "Diseño") {
          setRelacionLista(await getDisenos());
        } else if (formData.RelacionTipo === "Muestra") {
          setRelacionLista(await getMuestras());
        } else if (formData.RelacionTipo === "Material") {
          setRelacionLista(await getMateriales());
        } else {
          setRelacionLista([]);
        }
      } catch (err) {
        console.error("❌ Error cargando relación:", err);
      }
    };
    loadRelacion();
  }, [formData.RelacionTipo]);

  const handleSave = async () => {
    if (!formData.TituloId || !formData.ResponsableId) return;
    try {
      setSaving(true);
      let tarea;
      if (editingTarea) {
        tarea = await updateTarea(editingTarea.Id, formData);
        const tareaId = tarea?.Id ?? editingTarea.Id;
        const participanteIds = await syncParticipantes(
          tareaId,
          formData.ResponsableId,
        );
        setTareas((prev) =>
          prev.map((item) =>
            item.Id === tareaId
              ? { ...tarea, Id: tareaId, ParticipanteIds: participanteIds }
              : item,
          ),
        );
      } else {
        tarea = await createTarea(id, formData);
        const tareaId = tarea?.Id ?? tarea?.id;
        const participanteIds = await syncParticipantes(
          tareaId,
          formData.ResponsableId,
        );
        setTareas((prev) => [
          ...prev,
          { ...tarea, ParticipanteIds: participanteIds },
        ]);
      }
      resetDialog();
    } catch (err) {
      console.error("❌ Error al guardar tarea:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tareaId) => {
    await deleteTarea(tareaId);
    setTareas(tareas.filter((t) => t.Id !== tareaId));
  };

  const handleOpenNew = () => {
    setEditingTarea(null);
    setFormData({ ...emptyForm });
    setParticipantes([]);
    setInitialParticipanteIds([]);
    setOpenDialog(true);
  };

  const handleEdit = async (task) => {
    setEditingTarea(task);
    const responsableId = task.ResponsableId || "";
    setFormData({
      RelacionTipo: task.RelacionTipo || "",
      RelacionId: task.RelacionId || null,
      TituloId: task.TituloId,
      Descripcion: task.Descripcion,
      ResponsableId: responsableId,
      Estado: task.Estado,
      Prioridad: task.Prioridad,
      FechaInicio: task.FechaInicio
        ? dayjs(task.FechaInicio).format("YYYY-MM-DD")
        : task.FechaCreacion
          ? dayjs(task.FechaCreacion).format("YYYY-MM-DD")
          : "",
      FechaLimite: task.FechaLimite
        ? dayjs(task.FechaLimite).format("YYYY-MM-DD")
        : "",
    });
    setOpenDialog(true);
    try {
      const parts = await getTareasParticipantes(task.Id);
      const partsList = (Array.isArray(parts) ? parts : []).filter(
        (u) =>
          isSelectableUser(u) && Number(u.id) !== Number(responsableId),
      );
      setParticipantes(partsList);
      setInitialParticipanteIds(partsList.map((p) => Number(p.id)));
    } catch (err) {
      console.error("❌ Error cargando participantes:", err);
      setParticipantes([]);
      setInitialParticipanteIds([]);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "ResponsableId") {
      setParticipantes((prev) =>
        prev.filter((u) => Number(u.id) !== Number(value)),
      );
    }
  };

  const estadoOrder = { Pending: 1, Running: 2, Done: 3 };
  const orderedTareas = [...tareas].sort((a, b) => {
    if (estadoOrder[a.Estado] !== estadoOrder[b.Estado]) {
      return estadoOrder[a.Estado] - estadoOrder[b.Estado];
    }
    return a.Prioridad - b.Prioridad;
  });

  const prioridadLabels = {
    1: "High",
    2: "Medium",
    3: "Low",
  };

  return (
    <Box
      sx={{
        p: 3,
        ...contentMargin,
        width: "55em",
        backgroundColor: theme.palette.background.paper,
        height: "calc(100vh - 34px)",
        mt: 1,
        borderRadius: 2,
        boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      {/* 🟦 Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: theme.palette.text.primary }}
        >
          ToDo List – {proyectoNombre || "Cargando..."}
        </Typography>
        <IconButton
          onClick={handleOpenNew}
          sx={{
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            "&:hover": { backgroundColor: "#90b2b7", color: "#fff" },
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* 📋 Lista de tareas */}
      <List>
        {orderedTareas.map((t) => (
          <React.Fragment key={t.Id}>
            {/* 🧩 Tarea principal */}
            <ListItem
              sx={{
                borderLeft: `4px solid ${estadoColors[t.Estado] || "#ccc"}`,
                cursor: "pointer",
                mb: 1,
                borderRadius: 1,
                backgroundColor: `${estadoColors[t.Estado]}15`,
              }}
              onClick={() =>
                setTareaSeleccionada(tareaSeleccionada === t.Id ? null : t.Id)
              }
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {tareasDefinicion.find((d) => d.Id === t.TituloId)
                        ?.Nombre || "Sin título"}
                    </Typography>

                    {/* Estado */}
                    <Chip
                      size="small"
                      label={t.Estado}
                      sx={{
                        backgroundColor: `${estadoColors[t.Estado]}22`,
                        color: estadoColors[t.Estado],
                        fontWeight: 600,
                      }}
                    />

                    {/* Badge documentos */}
                    {docCounts[t.Id] > 0 && (
                      <Tooltip title="Documents attached">
                        <Chip
                          size="small"
                          label={`${docCounts[t.Id]}`}
                          sx={{
                            backgroundColor: "#2196f3",
                            color: "#ffffffff",
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {t.Descripcion || ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#444", fontWeight: 500 }}
                    >
                      {t.RelacionTipo === "Diseño" &&
                        `Diseño: ${
                          disenos.find((r) => r.id === t.RelacionId)?.nombre ||
                          `ID ${t.RelacionId}`
                        }`}
                      {t.RelacionTipo === "Muestra" &&
                        `Muestra: ${
                          muestras.find((r) => r.ID_MUESTRA === t.RelacionId)
                            ?.MUESTRA || `ID ${t.RelacionId}`
                        }`}
                      {t.RelacionTipo === "Material" &&
                        `Material: ${
                          materiales.find((r) => r.Id === t.RelacionId)
                            ?.NombreMaterial || `ID ${t.RelacionId}`
                        }`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#999" }}>
                      Responsible:{" "}
                      {usuarios.find((u) => u.id === t.ResponsableId)
                        ?.username || "—"}{" "}
                      |{" "}
                      <Box
                        component="span"
                        sx={{
                          color:
                            t.Prioridad === 1
                              ? "#d32f2f"
                              : t.Prioridad === 2
                              ? "#f57c00"
                              : "#388e3c",
                          fontWeight: 600,
                        }}
                      >
                        Priority: {prioridadLabels[t.Prioridad] || "Medium"}
                      </Box>{" "}
                      | Start:{" "}
                      {t.FechaCreacion
                        ? dayjs(t.FechaCreacion).format("DD/MM/YYYY")
                        : "—"}{" "}
                      | End:{" "}
                      {t.FechaLimite
                        ? dayjs(t.FechaLimite).format("DD/MM/YYYY")
                        : "—"}
                    </Typography>
                  </>
                }
              />

              <ListItemSecondaryAction>
                <IconButton
                  onClick={() => handleEdit(t)}
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    color: theme.palette.primary.main,
                    mr: 1,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() =>
                    setTareaSeleccionada(
                      tareaSeleccionada === t.Id ? null : t.Id
                    )
                  }
                  sx={{ border: "1px solid #2196f3", color: "#2196f3", mr: 1 }}
                >
                  <ArticleOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => setTareaAEliminar(t)}
                  sx={{ border: "1px solid #ff0000", color: "#ff0000" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>

            {/* 📎 Documentos de la tarea */}
            {/* 📎 Documentos de la tarea */}
            <AnimatePresence>
              {tareaSeleccionada === t.Id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box
                    sx={{
                      ml: 4,
                      mt: 1,
                      mb: 2,
                      borderLeft: "3px solid #ccc",
                      pl: 2,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <DocumentosTareas
                      id_Tarea={t.Id}
                      onCountChange={handleDocCountChange}
                    />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            <Divider />
          </React.Fragment>
        ))}
      </List>

      {/* 📝 Modal Nueva/Editar tarea */}
      <Dialog
        open={openDialog}
        onClose={resetDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
            overflow: "hidden",
            bgcolor: theme.palette.background.default,
          },
        }}
      >
        <Box sx={{ ...pageHeroShellSx(theme), mb: 0, borderRadius: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: { xs: 1.5, sm: 2 } }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.16),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.28),
                }}
              >
                <TaskAltOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1, lineHeight: 1.2 }}
                >
                  CRM
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.05rem", sm: "1.2rem" },
                  }}
                >
                  {editingTarea
                    ? t("ToDoList.EditTask")
                    : t("ToDoList.AddTask")}
                </Typography>
                {proyectoNombre ? (
                  <Chip
                    size="small"
                    label={proyectoNombre}
                    sx={{
                      mt: 0.5,
                      height: 22,
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      maxWidth: "100%",
                      bgcolor: alpha(accent, 0.1),
                      color: theme.palette.primary.dark,
                    }}
                  />
                ) : null}
              </Box>
            </Stack>
            {isMobile ? (
              <IconButton
                onClick={resetDialog}
                sx={{
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.8),
                  bgcolor: theme.palette.background.paper,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: 2,
            pb: { xs: 3, sm: 2.5 },
          }}
        >
          <Stack spacing={1.75}>
            <Box sx={sectionCardSx}>
              <Typography sx={pageSectionTitleSx}>
                {t("ToDoList.Tarea")}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("ToDoList.Tarea")}</InputLabel>
                  <Select
                    label={t("ToDoList.Tarea")}
                    value={formData.TituloId || ""}
                    onChange={(e) =>
                      handleFormChange("TituloId", Number(e.target.value))
                    }
                  >
                    {[...tareasDefinicion]
                      .sort((a, b) =>
                        a.Nombre.localeCompare(b.Nombre, "es", {
                          sensitivity: "base",
                        }),
                      )
                      .map((def) => (
                        <MenuItem key={def.Id} value={def.Id}>
                          {def.Nombre}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label={t("ToDoList.Descripcion")}
                  multiline
                  minRows={3}
                  value={formData.Descripcion}
                  onChange={(e) =>
                    handleFormChange("Descripcion", e.target.value)
                  }
                  sx={fieldSx}
                />
              </Stack>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography sx={pageSectionTitleSx}>
                {t("ToDoList.Responsable")}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 1.25 }}
              >
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("ToDoList.Responsable")}</InputLabel>
                  <Select
                    label={t("ToDoList.Responsable")}
                    value={formData.ResponsableId}
                    onChange={(e) =>
                      handleFormChange("ResponsableId", e.target.value)
                    }
                  >
                    {usuarios.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("ToDoList.Status")}</InputLabel>
                  <Select
                    label={t("ToDoList.Status")}
                    value={formData.Estado}
                    onChange={(e) =>
                      handleFormChange("Estado", e.target.value)
                    }
                    renderValue={() => (
                      <Chip
                        size="small"
                        label={
                          formData.Estado === "Done"
                            ? t("ToDoList.Done")
                            : formData.Estado === "Running"
                              ? t("ToDoList.Running")
                              : t("ToDoList.Pending")
                        }
                        sx={{
                          height: 24,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          bgcolor: alpha(
                            estadoColors[formData.Estado] ||
                              theme.palette.text.primary,
                            0.12,
                          ),
                          color:
                            estadoColors[formData.Estado] ||
                            theme.palette.text.primary,
                          border: "1px solid",
                          borderColor: alpha(
                            estadoColors[formData.Estado] ||
                              theme.palette.text.primary,
                            0.28,
                          ),
                        }}
                      />
                    )}
                  >
                    <MenuItem value="Pending">{t("ToDoList.Pending")}</MenuItem>
                    <MenuItem value="Running">{t("ToDoList.Running")}</MenuItem>
                    <MenuItem value="Done">{t("ToDoList.Done")}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Autocomplete
                multiple
                fullWidth
                size="small"
                sx={{ ...fieldSx, mt: 1.5 }}
                options={usuarios.filter(
                  (u) => Number(u.id) !== Number(formData.ResponsableId),
                )}
                value={participantes}
                onChange={(_, value) => setParticipantes(value || [])}
                getOptionLabel={(u) => u?.username || `User ${u?.id || ""}`}
                isOptionEqualToValue={(opt, val) =>
                  Number(opt.id) === Number(val.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Participants"
                    placeholder="Add people who can see this task"
                  />
                )}
              />

              <FormControl
                fullWidth
                size="small"
                sx={{ ...fieldSx, mt: 1.5 }}
              >
                <InputLabel>Priority</InputLabel>
                <Select
                  label="Priority"
                  value={formData.Prioridad}
                  onChange={(e) =>
                    handleFormChange("Prioridad", Number(e.target.value))
                  }
                >
                  <MenuItem value={1}>High</MenuItem>
                  <MenuItem value={2}>Medium</MenuItem>
                  <MenuItem value={3}>Low</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography sx={pageSectionTitleSx}>
                {t("ToDoList.objeto")}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 1.25 }}
              >
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("ToDoList.objeto")}</InputLabel>
                  <Select
                    label={t("ToDoList.objeto")}
                    value={formData.RelacionTipo}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        RelacionTipo: e.target.value,
                        RelacionId: null,
                      })
                    }
                  >
                    <MenuItem value="Diseño">{t("ToDoList.Diseño")}</MenuItem>
                    <MenuItem value="Muestra">{t("ToDoList.Muestra")}</MenuItem>
                    <MenuItem value="Material">
                      {t("ToDoList.Material")}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  disabled={!formData.RelacionTipo}
                >
                  <InputLabel>{t("ToDoList.Elemento")}</InputLabel>
                  <Select
                    label={t("ToDoList.Elemento")}
                    value={formData.RelacionId || ""}
                    onChange={(e) =>
                      handleFormChange("RelacionId", Number(e.target.value))
                    }
                  >
                    {relacionLista.map((item) => {
                      let label = "";
                      if (formData.RelacionTipo === "Diseño")
                        label = item.nombre || `Diseño ID ${item.id}`;
                      else if (formData.RelacionTipo === "Muestra")
                        label =
                          item.MUESTRA || `Muestra ID ${item.ID_MUESTRA}`;
                      else if (formData.RelacionTipo === "Material")
                        label =
                          item.NombreMaterial || `Material ID ${item.Id}`;
                      return (
                        <MenuItem
                          key={item.id || item.ID_MUESTRA || item.Id}
                          value={item.id || item.ID_MUESTRA || item.Id}
                        >
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography sx={pageSectionTitleSx}>
                {t("ToDoList.Start")} / {t("ToDoList.End")}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 1.25 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("ToDoList.Start")}
                  InputLabelProps={{ shrink: true }}
                  value={formData.FechaInicio || ""}
                  onChange={(e) =>
                    handleFormChange("FechaInicio", e.target.value)
                  }
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("ToDoList.End")}
                  InputLabelProps={{ shrink: true }}
                  value={formData.FechaLimite || ""}
                  onChange={(e) =>
                    handleFormChange("FechaLimite", e.target.value)
                  }
                  sx={fieldSx}
                />
              </Stack>
            </Box>
          </Stack>

          <Box
            sx={{
              position: { xs: "sticky", sm: "static" },
              bottom: 0,
              zIndex: 2,
              mt: 2,
              mx: { xs: -1.5, sm: 0 },
              px: { xs: 1.5, sm: 0 },
              pt: 1.5,
              pb: { xs: 1, sm: 0 },
              bgcolor: alpha(theme.palette.background.default, 0.92),
              backdropFilter: "blur(10px)",
              borderTop: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.7),
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{ width: "100%" }}
            >
              <Button
                onClick={resetDialog}
                disabled={saving}
                fullWidth={isMobile}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.8),
                }}
              >
                {t("ToDoList.Cancelar")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !formData.TituloId || !formData.ResponsableId}
                fullWidth={isMobile}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  color: theme.palette.primary.contrastText,
                  whiteSpace: "nowrap",
                }}
              >
                {editingTarea ? t("ToDoList.guardar") : t("ToDoList.AddTask")}
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ⚠️ Diálogo de eliminación */}
      <Dialog open={!!tareaAEliminar} onClose={() => setTareaAEliminar(null)}>
        <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this task?
            <br />
            <strong>
              {tareasDefinicion.find(
                (def) => def.Id === tareaAEliminar?.TituloId
              )?.Nombre || "Sin título"}
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTareaAEliminar(null)}
            sx={{ color: theme.palette.text.primary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              await deleteTarea(tareaAEliminar.Id);
              setTareas(tareas.filter((t) => t.Id !== tareaAEliminar.Id));
              setTareaAEliminar(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProyectoTareas;
